import styles from './recipient-input.module.css'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { UserSearchResult } from '@disco/api/server'
import { validateEmail } from '@disco/utils/validation'

import { Badge, Box, Flex as DsFlex, Text } from 'components/ds'

import useEventListener from 'hooks/use-event-listener'

import { RecipientDropdown } from './recipient-dropdown'
import { useRecipientSearch } from './use-recipient-search'

/**
 * Recipient tag interface for UI representation
 */
interface RecipientTag {
  /** Unique identifier for the recipient tag */
  id: string
  /** Email address of the recipient */
  email: string
  /** Whether the email address is valid */
  isValid: boolean
}

/**
 * Props for the RecipientInput component
 */
interface RecipientInputProps {
  /** Array of current recipient tags */
  recipients: RecipientTag[]
  /** Callback function when a new email is added */
  onAdd: (email: string) => void
  /** Callback function when a recipient is removed */
  onRemove: (id: string) => void
  /** Optional placeholder text for the input field */
  placeholder?: string
  /** Optional ARIA label for accessibility */
  'aria-label'?: string
}

const resultFilter = (result: UserSearchResult) =>
  ['email', 'user', 'saved', 'brief'].includes(result.type)

/**
 * RecipientInput - A multi-select email input component with autocomplete
 *
 * **⚠️ EXPERIMENTAL COMPONENT** - This component is experimental and created mainly to
 * experiment with replacement of react-select in multi-select/async creation mode.
 * It serves as a modern alternative to existing components like
 * `packages/frontend/src/views/components/common/email-groups/add-recipient/recipient-dropdown.tsx`
 * and uses the new design system components.
 *
 * This component provides a tag-based interface for adding and managing email recipients.
 * It features autocomplete suggestions from the DISCO user/business database, email validation,
 * keyboard navigation, bulk paste support, and accessibility support.
 *
 * Features:
 * - Multi-select email input with visual tags
 * - Real-time autocomplete suggestions
 * - Email validation with visual feedback
 * - Bulk paste support for delimiter-separated email lists (space, comma, semicolon, tab, newline)
 * - Keyboard navigation (arrows, enter, comma, backspace, tab)
 * - Click-to-remove recipient tags with hover states
 * - Container scrolling with arrow keys when dropdown is closed
 * - Accessibility compliance with ARIA attributes
 * - Debounced search (300ms) to optimize API calls
 * - Prevention of duplicate email addresses
 * - Built with new design system components
 *
 * Keyboard shortcuts:
 * - Enter/Comma: Add current input as recipient
 * - Backspace: Remove last recipient when input is empty
 * - Tab: Add current input and move focus
 * - Escape: Close dropdown and reset selection
 * - Up/Down arrows: Navigate dropdown or scroll recipient container
 *
 * Paste behavior:
 * - Paste single email: Normal paste behavior
 * - Paste multiple emails: Automatically parses and validates emails separated by:
 *   space, comma, semicolon, tab, or newline. Invalid emails are excluded.
 *
 * @param props - Component props
 * @returns RecipientInput component
 *
 * @example
 * ```tsx
 * <RecipientInput
 *   recipients={recipientTags}
 *   onAdd={(email) => addRecipient(email)}
 *   onRemove={(id) => removeRecipient(id)}
 *   placeholder="Enter email addresses..."
 *   aria-label="Brief recipients"
 * />
 * ```
 */
export const RecipientInput: React.FC<RecipientInputProps> = ({
  recipients,
  onAdd,
  onRemove,
  placeholder = 'Enter email addresses...',
  'aria-label': ariaLabel = 'Email recipients',
}) => {
  const [inputValue, setInputValue] = useState('')
  const [clickedOutside, setClickedOutside] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isSelectingFromDropdown = useRef(false)

  const { searchRecipients, suggestions, loading, apiError, cleanup } =
    useRecipientSearch({
      includeBlankEmails: false,
      hideBusinesses: false,
      includeBriefs: true,
      autoSuggest: true,
      resultFilter,
    })

  // Transform UserSearchResult[] to SearchOption[] format
  const dropdownOptions = useMemo(() => {
    return suggestions
      .map(suggestion => {
        // Create base SearchOption structure
        const baseOption = {
          id: suggestion.id.toString(),
          type: suggestion.type,
        }

        // Handle different types of UserSearchResult
        switch (suggestion.type) {
          case 'user':
            return {
              ...baseOption,
              value: suggestion.email,
              label: suggestion.name || suggestion.email,
              name: suggestion.name,
              email: suggestion.email,
            }
          case 'saved':
            return {
              ...baseOption,
              value: suggestion.name,
              label: suggestion.name,
              name: suggestion.name,
              email: '', // Saved groups don't have a single email, use empty string as placeholder
              recipients: suggestion.recipients,
            }
          case 'brief':
            return {
              ...baseOption,
              value: suggestion.name,
              label: suggestion.name,
              name: suggestion.name,
              email: '', // Briefs don't have a single email, use empty string as placeholder
              recipients: suggestion.recipients,
            }
          default:
            // Fallback for any other types (including 'email' and 'error')
            return {
              ...baseOption,
              value:
                (suggestion as any).email || (suggestion as any).name || '',
              label:
                (suggestion as any).name || (suggestion as any).email || '',
              name: (suggestion as any).name || '',
              email: (suggestion as any).email || '',
              ...((suggestion as any).type === 'error' && { error: true }),
            }
        }
      })
      .filter(option => {
        // Filter out options with emails that are already selected
        if (
          (option.type === 'saved' || option.type === 'brief') &&
          'recipients' in option
        ) {
          // For saved groups and briefs, check if all recipients are already selected
          // If all are selected, exclude the group/brief; if some are selected, keep it
          const allRecipientsSelected = option.recipients.every(recipient =>
            recipients.find(
              r => r.email.toLowerCase() === recipient.email.toLowerCase(),
            ),
          )
          return !allRecipientsSelected
        }

        // For individual users/emails, exclude if already selected
        return !recipients.find(
          r => r.email.toLowerCase() === option.email.toLowerCase(),
        )
      })
  }, [suggestions, recipients])

  const showDropdown = useMemo(() => {
    return (
      (loading ||
        dropdownOptions.length > 0 ||
        apiError ||
        validateEmail(inputValue.trim())) &&
      !clickedOutside
    )
  }, [loading, dropdownOptions.length, apiError, inputValue, clickedOutside])

  // Search suggestions when input changes
  useEffect(() => {
    if (inputValue.length >= 1) {
      setSelectedIndex(-1) // Reset selection when new search starts
      const timeoutId = setTimeout(() => {
        searchRecipients(inputValue, (_error, _result) => {
          // Results are handled by the hook's internal state
        })
      }, 300) // Debounce search

      return () => clearTimeout(timeoutId)
    } else {
      setSelectedIndex(-1)
    }
  }, [inputValue, searchRecipients])

  // Hide dropdown when there are no suggestions (unless loading, error, or valid email)
  // Also auto-select first suggestion when search results arrive
  useEffect(() => {
    if (
      !loading &&
      dropdownOptions.length === 0 &&
      inputValue.length >= 3 &&
      !validateEmail(inputValue.trim())
    ) {
      // Only hide dropdown if there are no suggestions AND no valid email
      // Note: We don't check !apiError here because we want to show "Add email" even with errors
      setSelectedIndex(-1)
    } else if (
      !loading &&
      !apiError &&
      dropdownOptions.length > 0 &&
      selectedIndex === -1
    ) {
      // Auto-select first suggestion when search results arrive
      setSelectedIndex(0)
    } else if (
      !loading &&
      dropdownOptions.length === 0 &&
      validateEmail(inputValue.trim()) &&
      selectedIndex === -1
    ) {
      // Auto-select the "Add email" option when it's the only option
      // This works for both no results and error cases
      setSelectedIndex(0)
    }
  }, [
    dropdownOptions,
    loading,
    apiError,
    inputValue.length,
    selectedIndex,
    inputValue,
  ])

  // Clean up on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  // Handle clicks outside to close dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setClickedOutside(true)
    } else if (
      containerRef.current &&
      containerRef.current.contains(event.target as Node)
    ) {
      // Check if the click was on the label element
      const clickedElement = event.target as HTMLElement
      if (
        clickedElement.tagName.toLowerCase() === 'label' ||
        clickedElement.closest('label')
      ) {
        setClickedOutside(true)
      }
    }
  }, [])

  useEventListener('mousedown', handleClickOutside)

  const getScrollContainer = useCallback(() => {
    return containerRef.current?.querySelector(
      `.${styles.inputContainer}`,
    ) as HTMLElement | null
  }, [])

  const scrollContainerToBottom = useCallback(() => {
    // Scroll the recipient container to bottom to show newly added emails
    const container = getScrollContainer()
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [getScrollContainer])

  /**
   * Execute an action while preserving the scroll position of the input container.
   * Useful for operations that might cause layout shifts (like removing recipients).
   */
  const withScrollPreservation = useCallback(
    (action: () => void) => {
      const container = getScrollContainer()
      const scrollTop = container?.scrollTop ?? 0
      action()
      setTimeout(() => {
        if (container) {
          container.scrollTop = scrollTop
        }
      }, 0)
    },
    [getScrollContainer],
  )

  const handleBlur = useCallback(() => {
    // Delay to allow dropdown clicks
    setTimeout(() => {
      if (
        inputValue.trim() &&
        validateEmail(inputValue.trim()) &&
        !isSelectingFromDropdown.current
      ) {
        onAdd(inputValue.trim())
        setInputValue('')
      }
      isSelectingFromDropdown.current = false
    }, 150)
  }, [inputValue, onAdd])

  /**
   * Parse a string containing multiple emails separated by delimiters
   * Supported delimiters: space, comma, semicolon, tab, newline
   * @param text - The text containing emails to parse
   * @returns Array of trimmed email strings
   */
  const parseEmailList = useCallback((text: string): string[] => {
    // Split by common delimiters: space, comma, semicolon, tab, newline
    const emails = text
      .split(/[\s,;\t\n]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0)

    return emails
  }, [])

  /**
   * Handle paste events to support bulk email addition
   * Parses pasted text for multiple emails and adds valid ones
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text')

      // Check if the pasted text contains multiple emails (has delimiters)
      const hasDelimiters = /[\s,;\t\n]/.test(pastedText)

      if (hasDelimiters) {
        e.preventDefault() // Prevent default paste behavior

        const emails = parseEmailList(pastedText)

        // Filter out empty strings and validate emails
        const validEmails = emails.filter(email => validateEmail(email))

        // Add all valid emails
        validEmails.forEach(email => {
          // Check for duplicates before adding
          const isDuplicate = recipients.some(
            r => r.email.toLowerCase() === email.toLowerCase(),
          )
          if (!isDuplicate) {
            onAdd(email)
          }
        })

        // Clear the input
        setInputValue('')

        // Scroll to bottom to show newly added emails
        setTimeout(() => {
          scrollContainerToBottom()
        }, 0)
      }
      // If no delimiters, allow default paste behavior (single email)
    },
    [parseEmailList, recipients, onAdd, scrollContainerToBottom],
  )

  const handleBadgeClick = useCallback(
    (e: React.MouseEvent, recipientId: string) => {
      e.preventDefault()
      withScrollPreservation(() => onRemove(recipientId))
      setTimeout(() => inputRef.current?.focus(), 0)
    },
    [onRemove, withScrollPreservation],
  )

  const handleContainerClick = (e: React.MouseEvent) => {
    // Don't interfere if clicking on the input directly
    if (e.target === inputRef.current) {
      return
    }

    // Focus the input and ensure it's actually focused
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleDropdownSelect = useCallback(
    option => {
      isSelectingFromDropdown.current = true
      if (option.type === 'saved' || option.type === 'brief') {
        option.recipients.forEach(recipient => {
          onAdd(recipient.email)
        })
      } else {
        onAdd(option.email)
      }
      setInputValue('')
      setSelectedIndex(-1)
      // Focus back to input and scroll to bottom after a short delay
      setTimeout(() => {
        inputRef.current?.focus()
        scrollContainerToBottom()
        isSelectingFromDropdown.current = false
      }, 0)
    },
    [onAdd, scrollContainerToBottom],
  )

  const handleCreateNew = useCallback(
    (email: string) => {
      isSelectingFromDropdown.current = true
      onAdd(email)
      setInputValue('')
      setSelectedIndex(-1)
      // Focus back to input and scroll to bottom after a short delay
      setTimeout(() => {
        inputRef.current?.focus()
        scrollContainerToBottom()
        isSelectingFromDropdown.current = false
      }, 0)
    },
    [onAdd, scrollContainerToBottom],
  )

  const handleInputFocus = () => {
    setClickedOutside(false)
  }

  // Check if dropdown is actually visible (when there are suggestions, loading, error, or valid email)
  const isDropdownVisible: boolean = showDropdown && !clickedOutside

  useEffect(() => {
    if (!isDropdownVisible) setSelectedIndex(-1)
  }, [isDropdownVisible])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isDropdownVisible && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault()
        const totalOptions =
          dropdownOptions.length +
          (inputValue.trim() && validateEmail(inputValue.trim()) ? 1 : 0)

        if (e.key === 'ArrowDown') {
          setSelectedIndex(prev => (prev < totalOptions - 1 ? prev + 1 : prev))
        } else if (e.key === 'ArrowUp') {
          setSelectedIndex(prev => (prev > -1 ? prev - 1 : -1))
        }
        return
      }

      // Handle scrolling the recipient container when dropdown is not visible
      if (
        !isDropdownVisible &&
        (e.key === 'ArrowDown' || e.key === 'ArrowUp')
      ) {
        e.preventDefault()
        const container = getScrollContainer()
        if (container) {
          const scrollAmount = 20
          container.scrollTop +=
            e.key === 'ArrowDown' ? scrollAmount : -scrollAmount
        }
        return
      }

      if (e.key === 'Escape') {
        setClickedOutside(true)
        return
      }

      if (e.key === 'Enter' || e.key === 'Tab') {
        // We shouldn't prevent default on Tab if the input is empty
        if (e.key !== 'Tab' || Boolean(inputValue.trim())) e.preventDefault()

        // If something is selected in dropdown, use that
        if (isDropdownVisible && selectedIndex >= 0) {
          if (
            dropdownOptions.length > 0 &&
            selectedIndex < dropdownOptions.length
          ) {
            // Selected a suggestion (only when there are actual suggestions)
            const selectedSuggestion = dropdownOptions[selectedIndex]
            handleDropdownSelect(selectedSuggestion)
          } else {
            // Selected the "Add email" option (when no suggestions or selected index >= suggestions.length)
            handleCreateNew(inputValue.trim())
          }
          setSelectedIndex(-1)
        } else if (inputValue.trim() && validateEmail(inputValue.trim())) {
          // Show dropdown with "Add email" option when search fails, returns no results, or has error
          // This gives users a choice rather than auto-adding
          setSelectedIndex(0) // Select the "Add email" option (index 0 when no suggestions)
        }
      }

      if (e.key === 'Backspace' && !inputValue && recipients.length > 0) {
        withScrollPreservation(() =>
          onRemove(recipients[recipients.length - 1].id),
        )
      }
    },
    [
      inputValue,
      recipients,
      onRemove,
      selectedIndex,
      dropdownOptions,
      handleDropdownSelect,
      handleCreateNew,
      isDropdownVisible,
      withScrollPreservation,
      getScrollContainer,
    ],
  )

  return (
    <>
      <Box ref={containerRef} className={styles.container}>
        <Text size="2" weight="medium" mb="2" as="label">
          Enter email addresses or search for contacts, mailing lists, or briefs
        </Text>

        <Box
          aria-label={ariaLabel}
          onClick={handleContainerClick}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              inputRef.current?.focus()
            }
          }}
          className={styles.inputContainer}
        >
          <DsFlex gap="1" align="start" wrap="wrap">
            {recipients.map(recipient => (
              <Badge
                key={recipient.id}
                variant="soft"
                color={recipient.isValid ? 'blue' : 'red'}
                role="button"
                tabIndex={0}
                aria-label={`Remove ${recipient.email}`}
                className={styles.recipientBadge}
                onClick={e => handleBadgeClick(e, recipient.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    withScrollPreservation(() => onRemove(recipient.id))
                  }
                }}
              >
                <span>{recipient.email}</span>
                <span className={styles.recipientRemove} aria-hidden="true">
                  ×
                </span>
              </Badge>
            ))}

            <input
              ref={inputRef}
              type="email"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={handleInputFocus}
              onPaste={handlePaste}
              placeholder={recipients.length === 0 ? placeholder : ''}
              aria-label="Add email address"
              aria-expanded={isDropdownVisible}
              aria-autocomplete="list"
              aria-controls="recipient-dropdown"
              role="combobox"
              className={styles.emailInput}
            />
          </DsFlex>
        </Box>

        <RecipientDropdown
          inputValue={inputValue}
          suggestions={dropdownOptions}
          loading={loading}
          onSelect={handleDropdownSelect}
          onCreateNew={handleCreateNew}
          showDropdown={isDropdownVisible}
          selectedIndex={selectedIndex}
          onMouseEnterOption={setSelectedIndex}
          apiError={apiError}
        />
      </Box>

      {/* Show validation errors */}
      {recipients.some(r => !r.isValid) && (
        <Text size="1" color="red" mt="1">
          Some email addresses are invalid and will not be included.
        </Text>
      )}
    </>
  )
}
